// src/pages/admin/Analytics.jsx
import React, { useEffect, useState } from "react";
import { supabase } from "../lib/supabase";
import { motion } from "framer-motion";
import { FiUsers, FiTrendingUp, FiCalendar } from "react-icons/fi";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { Link } from "react-router-dom";

const Analytics = () => {
  const [stats, setStats] = useState({ users: 0, requests: 0, appointments: 0 });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);

      const [{ count: userCount }, { count: requestCount }, { count: appointmentCount }] = await Promise.all([
        supabase.from("profiles").select("*", { count: "exact", head: true }),
        supabase.from("service_requests").select("*", { count: "exact", head: true }),
        supabase.from("appointments").select("*", { count: "exact", head: true }),
      ]);

      setStats({
        users: userCount || 0,
        requests: requestCount || 0,
        appointments: appointmentCount || 0,
      });

      // Example chart data (you can modify to show weekly/monthly trends)
      setChartData([
        { name: "Users", count: userCount || 0 },
        { name: "Requests", count: requestCount || 0 },
        { name: "Appointments", count: appointmentCount || 0 },
      ]);

      setLoading(false);
    };

    fetchAnalytics();
  }, []);

  if (loading) return <p className="text-center mt-20">Loading analytics...</p>;

  return (
    <div className="min-h-screen bg-gray-50 py-10 px-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Analytics Overview</h1>
        <p className="text-gray-600 mb-8">System metrics and performance insights</p>

        {/* Quick Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
          <div className="bg-white p-6 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Total Users</p>
              <p className="text-2xl font-bold text-gray-900">{stats.users}</p>
            </div>
            <FiUsers className="text-3xl text-blue-500" />
          </div>

          <div className="bg-white p-6 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Service Requests</p>
              <p className="text-2xl font-bold text-gray-900">{stats.requests}</p>
            </div>
            <FiTrendingUp className="text-3xl text-green-500" />
          </div>

          <div className="bg-white p-6 rounded-xl border flex justify-between items-center">
            <div>
              <p className="text-sm font-medium text-gray-600">Appointments</p>
              <p className="text-2xl font-bold text-gray-900">{stats.appointments}</p>
            </div>
            <FiCalendar className="text-3xl text-purple-500" />
          </div>
        </div>

        {/* Chart Section */}
        <div className="bg-white p-6 rounded-xl border shadow-sm">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Overview Chart</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={chartData}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="name" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="count" fill="#3b82f6" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="mt-6">
          <Link to="/dashboard" className="text-sm text-blue-600 hover:underline">
            ← Back to Dashboard
          </Link>
        </div>
      </motion.div>
    </div>
  );
};

export default Analytics;
